import React, { useContext, useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
    Layout,
    Typography,
    DatePicker,
    Button,
    PageHeader,
    Select,
    Row,
    Col,
    Table,
    Progress,
    Modal,
    Tooltip,
} from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { approvedUsersCount, getUsers, User } from "~/services/users";
import { getRegistrations, Registration, updateRegistration } from "~/services/registrations";
import { fetchLocations } from "~/services/locations";
import { TopNav, PoweredBy, DayList, DayListItem } from "~/components";
import moment, { Moment } from "moment";
import { AuthContext } from "~/providers/AuthProvider";
require("moment/locale/da");

const { Header, Footer, Content } = Layout;
const { Text } = Typography;

interface TParams {
    week: string;
}

type ParticipantListItem = {
    key: string;
    name: string;
    company: string;
    updated: Moment;
    guests: number;
};

export type SelectedDays = {
    [key: string]: {
        registered: boolean,
        guests: number
    };
}

export const Registrations = () => {
    const { week } = useParams<TParams>();
    const history = useHistory();
    const { isSuperAdmin } = useContext(AuthContext);

    const [selectedWeek, setSelectedWeek] = useState(
        moment(week).startOf("week")
    );

    const [users, setUsers] = useState<User[]>([]);
    const [usersCount, setUsersCount] = useState<number>(0);

    // Set selected user for manual registration form
    const [selectedUser, setSelectedUser] = useState<string | null>(null);


    // Used for registration form, manually registering users to lunch.
    const defaultSelectedDays: SelectedDays = {
        monday: {
            registered: false,
            guests: 0
        },
        tuesday: {
            registered: false,
            guests: 0
        },
        wednesday: {
            registered: false,
            guests: 0
        },
        thursday: {
            registered: false,
            guests: 0
        },
        friday: {
            registered: false,
            guests: 0
        },
    }
    const [selectedDays, setSelectedDays] = useState<SelectedDays>(defaultSelectedDays);

    const [isLoadingRegForm, setIsLoadingRegForm] = useState(false);

    const [registrations, setRegistrations] = useState<Registration[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
        undefined
    );
    const [participants, setParticipants] = useState<ParticipantListItem[]>([]);

    const [locationMap, setLocationMap] = useState({});

    const [locationsLoaded, setLocationsLoaded] = useState(false);

    const [selectedLocation, setSelectedLocation] = useState("all");

    const [showAddRegistration, setShowAddRegistration] = useState(false);

    const loadRegistrations = (date: Moment, locationFilter: string) => {
        setIsLoading(true);
        getRegistrations(date, locationFilter) // Now passing both parameters
            .then((registrations) => {
                fetchLocations().then((locations) => {
                    const locationMap = locations.reduce(
                        (map, loc) => ({
                            ...map,
                            [loc.id]: loc.address,
                        }),
                        {}
                    );

                    setLocationMap(locationMap);
                    setLocationsLoaded(true);

                    const filteredRegistrations = registrations
                        .map((reg) => {
                            // Assuming each registration includes user details and a list of days they are registered
                            return {
                                ...reg,
                                days: Object.keys(reg.days).reduce((result, day) => {
                                    const dayInfo = reg.days[day];
                                    // Check if the day's registration matches the selected location
                                    if (
                                        dayInfo.registered &&
                                        (locationFilter === "all" ||
                                            dayInfo.locationId === locationFilter)
                                    ) {
                                        result[day] = dayInfo;
                                    }
                                    return result;
                                }, {}),
                            };
                        })
                        .filter((reg) => Object.keys(reg.days).length > 0); // Only keep registrations that have any days left after filtering

                    setRegistrations(filteredRegistrations);
                    setIsLoading(false);
                });
            })
            .catch((error) => {
                console.error("Error loading data:", error);
                setIsLoading(false);
            });
    };

    useEffect(() => {
        approvedUsersCount().then((count) => {
            setUsersCount(count);
        });
        getCurrentUsers();

    }, []);

    useEffect(() => {
        setSelectedIndex(undefined);
        loadRegistrations(selectedWeek);
    }, [selectedWeek]);

    useEffect(() => {
        if (typeof selectedIndex !== "undefined" && registrations.length > 0) {
            let key: string = [
                "monday",
                "tuesday",
                "wednesday",
                "thursday",
                "friday",
            ][selectedIndex];
            let items: ParticipantListItem[] = [];
            let regs = registrations.filter((r) => r.days[key]);
            regs.map((r) => {
                items.push({
                    key: r.uid,
                    name: r.user?.name || "-",
                    updated: r.updated,
                    company: r.user?.company || "-",
                    guests: r.days[key].guests || 0,
                });
            });
            setParticipants(items);
        } else {
            setParticipants([]);
        }
    }, [selectedIndex, registrations]);

    useEffect(() => {
        loadRegistrations(selectedWeek, selectedLocation);
    }, [selectedWeek, selectedLocation]);


    useEffect(() => {
        setIsLoadingRegForm(true);
        setTimeout(() => {
            if (selectedUser) {
                const [name, company, uid] = selectedUser.split(";");
                // Get users current registration if any
                const selectedUserRegistration = registrations.find((registration) => registration.uid === uid);
                if (selectedUserRegistration) {
                    setSelectedDays({
                        monday: {
                            registered: selectedUserRegistration?.days?.monday?.registered || false,
                            guests: selectedUserRegistration?.days?.monday?.guests || 0
                        },
                        tuesday: {
                            registered: selectedUserRegistration?.days?.tuesday?.registered || false,
                            guests: selectedUserRegistration?.days?.tuesday?.guests || 0
                        },
                        wednesday: {
                            registered: selectedUserRegistration?.days?.wednesday?.registered || false,
                            guests: selectedUserRegistration?.days?.wednesday?.guests || 0
                        },
                        thursday: {
                            registered: selectedUserRegistration?.days?.thursday?.registered || false,
                            guests: selectedUserRegistration?.days?.thursday?.guests || 0
                        },
                        friday: {
                            registered: selectedUserRegistration?.days?.friday?.registered || false,
                            guests: selectedUserRegistration?.days?.friday?.guests || 0
                        },
                    });
                    setIsLoadingRegForm(false);
                } else {
                    setSelectedDays(defaultSelectedDays);
                    setIsLoadingRegForm(false);
                }
            } else {
                setSelectedDays(defaultSelectedDays);
                setIsLoadingRegForm(false);
            }
        }, 500);
    }, [selectedUser]);

    const getCurrentUsers = async () => {
        const users = await getUsers({ disabled: false });
        setUsers(users);
    }

    const handleDateChange = (date: Moment | null) => {
        let week = moment(date).startOf("week");
        setSelectedWeek(week);
        history.push(`/registrations/${moment(week).format("YYYY-MM-DD")}`);
    };

    const handleExportCSV = () => {
        if (!locationsLoaded) {
            alert("Please wait until location data has finished loading.");
            return;
        }

        const weekFormat = moment(selectedWeek).isoWeek();
        // Starting CSV content with UTF-8 Byte Order Mark (BOM)
        let csvContent = "\uFEFF";

        // Group registrations by location
        const registrationsByLocation = {};

        // Prepare data grouped by location and user
        registrations.forEach((reg) => {
            ["monday", "tuesday", "wednesday", "thursday", "friday"].forEach(
                (day) => {
                    let dayInfo = reg.days[day];
                    if (dayInfo && typeof dayInfo === "object" && dayInfo.registered) {
                        const location =
                            locationMap[dayInfo.locationId] || "";
                        const userKey = `${reg.user?.uid}-${location}`; // Unique key for each user and location
                        if (!registrationsByLocation[location]) {
                            registrationsByLocation[location] = {};
                        }
                        if (!registrationsByLocation[location][userKey]) {
                            registrationsByLocation[location][userKey] = {
                                name: reg.user?.name || "Unknown",
                                company: reg.user?.company || "Unknown",
                                days: {
                                    Monday: {
                                        registered: "No",
                                        guests: reg.days['monday']?.guests || 0,
                                    },
                                    Tuesday: {
                                        registered: "No",
                                        guests: reg.days['tuesday']?.guests || 0,
                                    },
                                    Wednesday: {
                                        registered: "No",
                                        guests: reg.days['wednesday']?.guests || 0,
                                    },
                                    Thursday: {
                                        registered: "No",
                                        guests: reg.days['thursday']?.guests || 0,
                                    },
                                    Friday: {
                                        registered: "No",
                                        guests: reg.days['friday']?.guests || 0,
                                    },
                                },
                            };
                        }
                        registrationsByLocation[location][userKey].days[
                            day.charAt(0).toUpperCase() + day.slice(1)
                        ].registered = "Yes";
                    }
                }
            );
        });

        // Process each location group
        Object.keys(registrationsByLocation).forEach((location) => {
            // Add location header with the week number
            csvContent += `Week ${weekFormat};${location};\nName;Company;Monday;Tuesday;Wednesday;Thursday;Friday\n`;

            const userRegs = registrationsByLocation[location];
            type DayKey = "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday";
            const totals: Record<DayKey, number> = {
                Monday: 0,
                Tuesday: 0,
                Wednesday: 0,
                Thursday: 0,
                Friday: 0,
            };
            const guestTotals: Record<DayKey, number> = {
                Monday: 0,
                Tuesday: 0,
                Wednesday: 0,
                Thursday: 0,
                Friday: 0,
            };

            Object.values(userRegs).forEach((reg) => {
                const daysData = reg.days;
                let dayValues = "";
                Object.keys(daysData).forEach((day) => {
                    const guests = daysData[day].guests > 0 ? ` (${daysData[day].guests})` : "";
                    dayValues += `${daysData[day].registered}${guests};`;
                })
                csvContent += `${reg.name};${reg.company};${dayValues}\n`;


                // Count totals for each day
                Object.keys(daysData).forEach((day) => {
                    const dayKey = day as DayKey;
                    if (daysData[day].registered === "Yes") totals[dayKey]++;
                    if (daysData[day].guests > 0) {
                        totals[dayKey] += daysData[day].guests;
                        guestTotals[dayKey] += daysData[day].guests;
                    }
                });
            });

            const guestMondayTotal = guestTotals.Monday > 0 ? `(${guestTotals.Monday})` : "";
            const guestTuesdayTotal = guestTotals.Tuesday > 0 ? `(${guestTotals.Tuesday})` : "";
            const guestWednesdayTotal = guestTotals.Wednesday > 0 ? `(${guestTotals.Wednesday})` : "";
            const guestThursdayTotal = guestTotals.Thursday > 0 ? `(${guestTotals.Thursday})` : "";
            const guestFridayTotal = guestTotals.Friday > 0 ? `(${guestTotals.Friday})` : "";

            // Append the totals row for this location
            csvContent += `Total;${location};${totals.Monday}${guestMondayTotal};${totals.Tuesday}${guestTuesdayTotal};${totals.Wednesday}${guestWednesdayTotal};${totals.Thursday}${guestThursdayTotal};${totals.Friday}${guestFridayTotal}\n\n`;
        });

        // Create and download the CSV file
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `registrations-week-${weekFormat}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSubmitManualRegistration = () => {
        setSelectedUser(null)
        setSelectedDays(defaultSelectedDays)
        setShowAddRegistration(false)
        selectedUser && updateRegistration(selectedWeek, selectedUser.split(";")[2], selectedDays).then(() => {
            // Reload registrations after updating for show the new data on the table
            loadRegistrations(selectedWeek, selectedLocation);
        });
    }

    const handleCancelManualRegistration = () => {
        // Reset selected user and days, works when closing modal, cancelling or clicking outside of modal.
        setSelectedUser(null)
        setSelectedDays(defaultSelectedDays)
        setShowAddRegistration(false)
    }

    // setup table columns
    const columns = [
        {
            title: "Navn",
            key: "name",
            sorter: (a: User, b: User) => a.name.localeCompare(b.name),
            render: (text: string, record: ParticipantListItem) => (
                <Text>{record.name}</Text>
            ),
        },
        {
            title: "Company",
            key: "company",
            sorter: (a: User, b: User) => a.company.localeCompare(b.company),
            render: (text: string, record: ParticipantListItem) => (
                <Text>{record.company}</Text>
            ),
        },
        {
            title: "Guests",
            key: "guests",
            render: (text: string, record: ParticipantListItem) => (
                <Text>{record.guests > 0 ? record.guests : ''}</Text>
            ),
        },
    ];

    if (isSuperAdmin) {
        columns.push({
            title: "Handling",
            key: "function",
            render: (record: ParticipantListItem) => (
                <Button
                    key="delete"
                    type="danger"
                    onClick={() => showDeleteConfirm(record.key, record.name)}
                    disabled={!locationsLoaded || isLoading}
                >Slet tilmelding</Button>
            ),
        });
    }

    const showDeleteConfirm = (userId: string, userName: string) => {
        Modal.confirm({
            title: `Er du sikker på at du vil slette tilmeldingen for ${userName}?`,
            content: "Når slettet, kan dette ikke gøres om.",
            okText: "Ja, afmeld",
            okType: "danger",
            cancelText: "Nej, fortryd",
            onOk() {
                const selectedUserRegistration = registrations.find((registration) => registration.uid === userId);

                const newRegistration = {
                    monday: selectedIndex === 0 ? false : selectedUserRegistration?.days?.monday?.registered || false,
                    tuesday: selectedIndex === 1 ? false : selectedUserRegistration?.days?.tuesday?.registered || false,
                    wednesday: selectedIndex === 2 ? false : selectedUserRegistration?.days?.wednesday?.registered || false,
                    thursday: selectedIndex === 3 ? false : selectedUserRegistration?.days?.thursday?.registered || false,
                    friday: selectedIndex === 4 ? false : selectedUserRegistration?.days?.friday?.registered || false,
                }

                updateRegistration(selectedWeek, userId, newRegistration).then(() => {
                    // Reload registrations after updating for show the new data on the table
                    loadRegistrations(selectedWeek, selectedLocation);
                });
            },
        });
    };

    const showManualRegistrationConfirm = () => {
        const name = selectedUser ? selectedUser.split(";")[0] || "" : "";
        Modal.confirm({
            title: `Er du sikker på at du vil tilføje/opdater tilmeldingen for ${name}?`,
            content: "Når ændret, kan dette ikke gøres om.",
            okText: "Ja, tilmeld/opdater",
            okType: "danger",
            cancelText: "Nej, fortryd",
            onOk() {
                handleSubmitManualRegistration()
            },
        });

    }


    const percent =
        usersCount > 0
            ? Math.floor(Math.min(100, (100 / usersCount) * registrations.length))
            : 0;

    return (
        <Layout>
            <Header>
                <TopNav />
            </Header>
            <Content style={{ padding: "0 50px" }}>
                <div id="registrations">
                    <PageHeader
                        title={"Uge " + moment(selectedWeek).isoWeek()}
                        subTitle={
                            moment(selectedWeek).format("D. MMM") +
                            " - " +
                            moment(selectedWeek).add(6, "days").format("D. MMM")
                        }
                        extra={[
                            <Button
                                key="previousweek"
                                type="primary"
                                shape="circle"
                                disabled={isLoading}
                                onClick={() =>
                                    handleDateChange(moment(selectedWeek).subtract(1, "week"))
                                }
                            >
                                <LeftOutlined />
                            </Button>,
                            <Button
                                key="nextweek"
                                type="primary"
                                shape="circle"
                                disabled={isLoading}
                                onClick={() =>
                                    handleDateChange(moment(selectedWeek).add(1, "week"))
                                }
                            >
                                <RightOutlined />
                            </Button>,
                            <DatePicker
                                key="selectedweek"
                                disabled={isLoading}
                                picker="week"
                                value={selectedWeek}
                                allowClear={false}
                                onChange={(date) => handleDateChange(date)}
                            />,
                            <Select
                                defaultValue="all"
                                style={{ width: 200 }}
                                onChange={(value) => {
                                    setSelectedLocation(value);
                                    loadRegistrations(selectedWeek, value);
                                }}
                            >
                                <Select.Option value="all">All Locations</Select.Option>
                                {Object.entries(locationMap).map(([id, address]) => (
                                    <Select.Option key={id} value={id}>
                                        {address}
                                    </Select.Option>
                                ))}
                            </Select>,
                            <Button
                                key="export"
                                type="primary"
                                onClick={handleExportCSV}
                                disabled={!locationsLoaded || isLoading}
                            >
                                Opret .csv fil
                            </Button>,
                        ]}
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <DayList
                                    loading={isLoading}
                                    selectedWeek={selectedWeek}
                                    selectedIndex={selectedIndex}
                                    registrations={registrations}
                                    onClick={(day: DayListItem, index: number) => {
                                        setSelectedIndex(index);
                                    }}
                                />
                                <Progress
                                    percent={percent}
                                    format={(percent) =>
                                        `${registrations.length}/${usersCount || 0}`
                                    }
                                    showInfo={usersCount > 0}
                                />

                                {
                                    isSuperAdmin && (
                                        <>
                                            <Button
                                                style={{ marginTop: "32px" }}
                                                key="add-registration"
                                                type="primary"
                                                onClick={() => setShowAddRegistration(true)}
                                                disabled={!locationsLoaded || isLoading}
                                            >+ Manuel tilmelding</Button>

                                            <Modal
                                                title="Manuel tilmelding"
                                                visible={showAddRegistration}
                                                onOk={showManualRegistrationConfirm}
                                                okText="Tilmeld/Opdater"
                                                cancelText="Annuller"
                                                onCancel={handleCancelManualRegistration}
                                            >
                                                <b>{"Uge " + moment(selectedWeek).isoWeek()}</b>
                                                ({moment(selectedWeek).format("D. MMM") + " - " + moment(selectedWeek).add(6, "days").format("D. MMM")})
                                                <Select
                                                    showSearch={true}
                                                    value={selectedUser}
                                                    placeholder="Vælg bruger"
                                                    style={{ width: '100%', marginBottom: '16px' }}
                                                    onChange={(value) => setSelectedUser(value)}
                                                >
                                                    <Select.Option value="">Vælg bruger</Select.Option>
                                                    {Object.entries(users).map(([id, user]) => (
                                                        <Select.Option key={id} value={user.name + ';' + user.company + ';' + user.uid}>
                                                            {user.name} - {user.company}
                                                        </Select.Option>
                                                    ))}
                                                </Select>

                                                {
                                                    selectedUser && (
                                                        <DayList
                                                            loading={isLoadingRegForm}
                                                            selectedWeek={selectedWeek}
                                                            selectedIndex={selectedIndex}
                                                            registrations={registrations}
                                                            registrationForm={true}
                                                            selectedDays={selectedDays}
                                                            setSelectedDays={setSelectedDays}
                                                        />
                                                    )
                                                }

                                            </Modal>
                                        </>
                                    )
                                }


                            </Col>
                            <Col span={12}>
                                {typeof selectedIndex !== "undefined" ? (
                                    <Table
                                        loading={isLoading}
                                        columns={columns}
                                        dataSource={participants}
                                        pagination={false}
                                        locale={{ emptyText: "Ingen tilmeldinger" }}
                                    />
                                ) : null}
                            </Col>
                        </Row>
                    </PageHeader>
                </div>
            </Content>
            <Footer>
                <PoweredBy />
            </Footer>
        </Layout>
    );
};
